import React, { useState } from "react";
import {
  Box, Paper, Typography, Button, Grid, Chip, CircularProgress,
  Card, CardContent, List, ListItem, ListItemIcon, ListItemText,
} from "@mui/material";
import { Check, Star } from "@mui/icons-material";
import { createSmbCheckout } from "../../../api/smb.api";

const plans = [
  {
    id: "4",
    name: "SMB Max",
    price: "$249",
    period: "/month",
    features: [
      "Unlimited AI tools access",
      "125 min video generation",
      "Unlimited image & audio generation",
      "Create & host website",
      "AI calling agents",
      "1000 min calling credit",
      "Track up to 15 websites",
      "Relationship manager",
    ],
    recommended: false,
  },
  {
    id: "5",
    name: "SMB Max Managed",
    price: "$499",
    period: "/month",
    features: [
      "Everything in SMB Max",
      "250 min video generation",
      "Personal avatar video",
      "Professional social media management",
      "Dedicated account manager",
      "Priority content review",
      "Custom integrations support",
    ],
    recommended: true,
  },
];

const PaymentStep = ({ businessId }) => {
  const [selectedPlan, setSelectedPlan] = useState("4");
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      const result = await createSmbCheckout(selectedPlan);
      if (result?.status && result?.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3, textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Choose Your Plan</Typography>
        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
          Pick the plan that fits your business. Cancel anytime.
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} key={plan.id}>
            <Card
              onClick={() => setSelectedPlan(plan.id)}
              sx={{
                bgcolor: "#1B1D2A",
                border: `2px solid ${selectedPlan === plan.id ? "#3B82F6" : "#282D51"}`,
                borderRadius: 3,
                cursor: "pointer",
                transition: "border-color 0.2s",
                position: "relative",
                "&:hover": { borderColor: "#3B82F6" },
              }}
            >
              {plan.recommended && (
                <Chip
                  icon={<Star sx={{ fontSize: 14 }} />}
                  label="Recommended"
                  size="small"
                  sx={{
                    position: "absolute", top: 12, right: 12,
                    bgcolor: "#F59E0B", color: "#000", fontWeight: 600,
                  }}
                />
              )}
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{plan.name}</Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", mb: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: "#3B82F6" }}>{plan.price}</Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF", ml: 0.5 }}>{plan.period}</Typography>
                </Box>
                <List dense disablePadding>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.3 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Check sx={{ fontSize: 16, color: "#10B981" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: "body2", sx: { color: "#E5E7EB" } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Button
        variant="contained"
        onClick={handleCheckout}
        disabled={isLoading}
        fullWidth
        sx={{
          py: 2, bgcolor: "#3B82F6", "&:hover": { bgcolor: "#2563EB" },
          textTransform: "none", fontWeight: 700, fontSize: "1.1rem", borderRadius: 2,
        }}
      >
        {isLoading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Proceed to Payment"}
      </Button>
    </Box>
  );
};

export default PaymentStep;
